import React, { useState } from 'react';  // import the React library
import '../App.css'; // import your CSS file
import { motion } from 'framer-motion';
import BioLine from '../articleComponents/bioLine.js'; // import your CSS file
import Connect from '../articleComponents/connect';
import SocialMedia from '../articleComponents/SocialMedia';
import MetaTags from '../mainPageComponents/MetaTags';
import Gallery from '../articleComponents/imageGallery.js';
import TraversePosts from '../articleComponents/navigation';
import '../articleComponents/imageGallery.css';
import state1 from '../images/state-1.jpg';
import state2 from '../images/state-2.jpg';
import state3 from '../images/state-3.jpg';
import Image from '../mainPageComponents/image';

const today = new Date("1, 24, 2021").toLocaleDateString('en-US', {  
  day:   'numeric',
  month: 'short',
  year:  'numeric',
  weekday: 'long'
});

function AStateIsBorn() {

    const images = [
        'https://live.staticflickr.com/65535/50870085797_f3589da6f2_c.jpg',
        'https://live.staticflickr.com/65535/50870085772_1a85d774a5_c.jpg',
        'https://live.staticflickr.com/65535/50869989791_ef374efee8_c.jpg',
        'https://live.staticflickr.com/65535/50870085732_a06586f412_c.jpg'
      ];
      
        // setting state to establish the main pic
        const [selectedImg, setSelectedImg] = useState(images[0]);

  return (
      <motion.div className="articleStyle"
        initial={{ opacity: 0 }}
animate={{opacity: 1 }}
exit={{ opacity: 0}}
transition={{duration: .4}}
>

<MetaTags title="A State is Born" desc="Learning useState() in ReactJS for the first time is like learning how to ride a bike. Next step: Road trip to the ends of the earth and back." 
URL="https://highway-60.web.app/articles/a-state-is-born" imageURL="/images/a-new-peak.jpg" />

<h1 id="top" style={{marginTop: '2em'}}>A State is Born - Learning useState() in ReactJS</h1>

<Gallery images={images} selectedImg={selectedImg} setSelectedImg={setSelectedImg}/>

<BioLine />
<p>{today}</p>

<div style={{textAlign: "left", marginTop: "5px", marginBottom: "20px"}}>
<SocialMedia url="https://highway-60.web.app/articles/a-state-is-born" title="Highway 60" hashtag="#highway60" hashtagArray={["coding", "writing"]} via="TheHighway60" desc="Learning useState() in ReactJS for the first time is like learning how to ride a bike. Next step: Road trip to the ends of the earth and back." />

</div>

    <p>
    Props and state have been pretty difficult. The first few times I go over them, I just can’t wrap my head around it. Granted, I spent a few months learning to code without actually <i>doing</i> any code. I saw the videos and took lots of notes. 
      </p>

<p>
I told a developer at work what I was doing and in a classical developer way said right to my face, without any emotion whatsoever, 
</p>

<p>
“You are doing nothing.”
</p>

<p>
My experience with <i>props</i> and <i>state</i> proves his point. Unless I am actually doing the things I am learning, it is just an intellectual exercise, and a lot of what I should understand I clearly don’t get. 
</p>

<p>
Props came a little bit easier. I have done so many redundant things over the years, creating a template that just needed it’s own unique values was a solution begging to be used. 
</p>

<p>
State is a little more complicated. 
</p>

<p>
Props is like a little boy running from his room to you in a t-shirt and boxers. Depending on the weather, what he will be doing that day, and what he feels like, you put on the rest of his clothes before he goes to school. 
</p>

<p>
setState() is what happens once he gets to school and has to change his shoes and pants for gym class. 
</p>

<p>
useState() is what happens when he goes from learning one subject to another and has to replace every book on his desk for each class. 
</p>

<p>
Today I started with setState(). After all, it would be pretty embarrassing if I were to attend 5th grade geography in just my boxers, right?
</p>

<h1>
The Highway 60 Website
</h1>

<p>
<b>setState()</b> is a function that performs a shallow merge between the new and the previous state. Set the state to what you want to see <i>after the event that triggers the state change has fired</i>. 
</p>

<p>
Take a look at the home page of my app:
</p>

<Image picURL={state1} alt="The picture of highway 60 home page pre-useState()" />

<p>
Yes, I know. It needs some serious styling. After I put this state and props to rest, I have a little google analytics and SEO work, then it’s on to CSS and Bootstrap. 
</p>

<p>
Then more serious coding, like, ummm, JavaScript. 
</p>

<p>
There is no state because I haven’t set it yet. 
</p>

<p>
What’s the point of putting all my posts on the home page when there is a menu item called “blog”?
</p>

<p>
How about we change the initial state of the home page to an &#60;h1&#62; tag saying <u>Click to see Highway 60 Posts</u>?
</p>

<p>
When the user clicks on the element, the onClick function will mount the &#60;ArticleList /&#62; component and you will see the articles. 
</p>

<p>
Let’s try. 
</p>

<p>
I need the &#123; Component &#125; hook alongside my React when calling ‘react’
</p>

<div style={{textAlign: "left", marginLeft: "15px", fontSize: "1.25em"}}>
    <br />
<code>{`
import React, { Component } from 'react';  
`}</code><br />
<br />
</div>

<p>
Before we touch the component itself, let’s create a style object for the text:
</p>

<div style={{textAlign: "left", marginLeft: "15px", fontSize: "1.25em"}}>
    <br />
<code>{`
const Styles = {
`}</code><br />
<code>{`
  fontSize: "3em",
`}</code><br />
<code>{`
  fontColor: 'blue'
`}</code><br />
<code>{`
  } 
`}</code><br />
<br />
</div>

<p>
Inside the JSX text, let’s redo the &#60;h1&#62; title text and insert our style variable object. The &#60;h1&#62; text is still raw, but we will get to that in a minute. 
</p>

<div style={{textAlign: "left", marginLeft: "15px", fontSize: "1.25em"}}>
    <br />
<code>{`
<h1 style={{Styles}}>Click to See Highway 60 Posts</h1>      
`}</code><br />
<br />
</div>

<p>
Only after the state changes upon an onClick() event to the &#60;h1&#62; element will we set the setState to &#60;ArticleList /&#62;.
</p>

<p>
Let’s code a little bit. Here we are using only setState, so we can go with a Class component. 
</p>

<div style={{textAlign: "left", marginLeft: "15px", fontSize: "1.25em"}}>
    <br />
<code>{`
 class Home extends React.Component {
`}</code><br />
<code>{`
constructor(props){
`}</code><br />
<code>{`
  super(props)
`}</code><br />
<code>{`

`}</code><br />
<code>{`
  this.state = {
`}</code><br />
<code>{`
message: "Click here to see the Highway 60 posts",
`}</code><br />
<code>{`
articles: ""
`}</code><br />
<code>{`
  }
`}</code><br />
<code>{`
}
`}</code><br />
<br />
</div>

<p>
<b>Remember!!!</b> Since all of the state code and the JSX render code is <b><i>all in one big class called Home, when you call the method, use this.method to call it!</i></b>
</p>

<p>
We set the properties and values for the initialized sate. this.state.message is the &#60;h1&#62; text and this.state.articles is the &#60;div&#62; right under it that starts out empty at “”.
</p>

<p>
Let’s put in these states in the area of our page where all this will happen. 
</p>

<div style={{textAlign: "left", marginLeft: "15px", fontSize: "1.25em"}}>
    <br />
<code>{`
<div onClick={this.ArticleList}>
`}</code><br />
<code>{`
<h1 style={{fontSize: "3em", textDecoration: "underline"}}>{this.state.message}</h1>      
`}</code><br />
<code>{`
{this.state.articles}
`}</code><br />
<code>{`
</div>
`}</code><br />
<br />
</div>

<p>
Woa! Where did this.ArticleList come from? Why are the &#60;div&#62; tags wrapped around the &#60;h1&#62; tag?
</p>

<p>
I wrapped the &#60;div&#62; tags around everything so there would be more space for the user to click to activate the function this.ArticleList. 
</p>

<p>
Inside the &#60;div&#62; tag is an onClick() event handler. It will run this.ArticleList which will <b><i>change the state of this &#60;div&#62; by removing the &#60;h1&#62; title text and inserting the &#60;ArticleList /&#62; component carrying all of Highway 60’s articles.</i></b> 
</p>

<p>
Yes, once I am done with CSS and Bootstrap, I will have to learn about pagination. In the meantime, here is my onClick() event handler that changes state. 
</p>

<div style={{textAlign: "left", marginLeft: "15px", fontSize: "1.25em"}}>
    <br />
<code>{`
ArticleList = () => {
`}</code><br />
<code>{`
  this.setState({
`}</code><br />
<code>{`
    message: "",
`}</code><br />
<code>{`
 articles: <ArticleList />
`}</code><br />
<code>{`
  })
`}</code><br />
<code>{`
}
`}</code><br />

<br />
</div>

<p>
this.state.message goes empty and this.state.articles becomes the component that has all the articles. 
</p>

<p>
To manage issues, I have NPM START running in my CLI terminal below and it says there are no errors so this is working. 
</p>

<p>
Here is a peek at the homepage. 
</p>

<Image picURL={state2} alt="The picture of highway 60 home page with useState()" />

<p>
Just click on the yellow highlight and we will see the next state of the site:
</p>

<Image picURL={state3} alt="The picture of highway 60 home page with useState() and it works!" />

<p>
BUL! (Hebrew for YESH!)
</p>

<p>
It worked. 
</p>

<p>
Since the “Highway 60 Posts:” has a cool animation attached to it, once you click, the animation fires, making a nice effect. 
</p>

<p>
It’s good to know that you can change state in two areas, the &#60;h1&#62; tags and the &#60;ArticleList /&#62; component below. The example might be simple, but conceptually it’s good to internalize just how powerful this can be. 
</p>

<p>
You can also make multiple setState() functions to handle different events to change the state of any element, property, value you need. 
</p>


<TraversePosts prevURL="/articles/go-with-God" nextURL="../articles"/>

<Connect />
</motion.div>
  );
}

export default AStateIsBorn;
